import * as React from 'react';
import { ChartContext, GenericChartComponent, getMouseCanvas, isDefined } from '@react-financial-charts/core';
import { isHoverForInteractiveType, saveNodeType, terminate } from 'react-financial-charts';
import { EachInteractiveOrderLine, PriceLine } from './Wrapper';
import { TPSLRatioBox, calcRatioBoxWidth } from './Wrapper/TPSLRatioBox';
import { calcLabelWidth } from './Wrapper/EachInteractiveOrderLine';
import { calcEntryBoxWidth } from './Wrapper/OrderEntryBox';
import { drawCancelIcon, roundedRect } from '@/utils/canvasUtils';
import type { MoreProps, RatioDropdown, OrderLineRenderNode } from '../../../types/commontypes';
import { COLORS } from '@/constants/colors';
import { TPSLRatio, calculateTPAndSL, netRoeToPrice, priceToNetRoe } from '@/utils/calculateSLndTP';
import { globalShapeStore } from '@/stores/globalShapeStore';
import { ORDER_LINE_RENDER_DEFAULTS } from '@/utils/shapeConverter';

// 헷지 X 버튼 크기 — entry box와 동일한 높이, 아이콘만 들어가는 최소 폭
const HEDGE_DELETE_BTN_H = 24; // BOX_HEIGHT
const HEDGE_DELETE_BTN_W = 22; // ICON_SIZE(14) + H_PAD(4) * 2
const HEDGE_DELETE_BTN_RADIUS = 4; // RADIUS

// 헷지 드래그 핸들 — Y축 왼쪽 30px 고정
const HEDGE_DRAG_HANDLE_W = 22;
const HEDGE_DRAG_HANDLE_H = 24;
const HEDGE_DRAG_HANDLE_RADIUS = 4;
const HEDGE_DRAG_HANDLE_RIGHT = 30; // Y축 왼쪽 30px

interface InteractiveOrderLineProps {
  readonly onChoosePosition?: (e: React.MouseEvent, newText: unknown, moreProps: MoreProps) => void;
  readonly onDragComplete: (
    e: React.MouseEvent | null,
    newAlertList: unknown[],
    moreProps: MoreProps,
    draggedAlert: unknown,
  ) => void;
  readonly onSelect?: (e: React.MouseEvent, interactives: unknown[], moreProps: MoreProps) => void;
  readonly onDelete?: (
    e: React.MouseEvent | null,
    yCoordinate: OrderLineRenderNode,
    moreProps: MoreProps,
    type: 'Entry' | 'TP' | 'SL',
  ) => void;
  readonly isDark?: boolean;
  readonly setRatioDropdown: (data: Partial<RatioDropdown>) => void;
  /** globalShapeStore.lastTPSLByDirection — ShapeRender에서 구독 후 전달 (re-render 트리거용) */
  readonly lastTPSLByDirection?: { long: number; short: number; hedge: number };
  readonly onDeleteAll?: () => void;
  readonly orderLineList: OrderLineRenderNode[];
  readonly enabled: boolean;
  readonly onDoubleClick?: (e: React.MouseEvent, uuid: string, moreProps: MoreProps) => void;
  readonly ratioDropdown?: RatioDropdown;
}

/**
 * OrderLine 드래그 override.
 *
 * 다른 도형들은 `DragOverrideBase`(`uuid: string`)를 베이스로 통일됐지만,
 * OrderLine은 두 가지 이유로 별도 패턴을 유지한다:
 *
 * 1. 식별자가 **array index**: `orderLineList[index]`로 직접 lookup하는 호출이 많아
 *    uuid 기반으로 바꾸면 다섯 곳 이상의 헷지 페어 매칭·동기화 로직을 함께 수정해야 함.
 * 2. **Entry/TP/SL 핸들 분기**: 한 OrderLine 안에 세 종류의 드래그 핸들이 있어
 *    "어느 도형(uuid)" 외에 "어느 핸들(type)"도 식별해야 함.
 */
interface DragOverride {
  index: number;
  type: 'Entry' | 'TP' | 'SL';
  yValue?: number;
  tpslPrice?: Array<{ tp: number; sl: number }>;
}

interface InteractiveOrderLineState {
  current?: Record<string, unknown>;
  override?: DragOverride;
}

export class InteractiveOrderLine extends React.Component<InteractiveOrderLineProps, InteractiveOrderLineState> {
  public static defaultProps = {
    orderLineList: [],
  };

  public static contextType = ChartContext;

  // RFC 내부 시스템이 saveNodeType으로 등록된 ref를 통해 인스턴스 메서드를 직접 호출한다.
  // TypeScript는 "선언만 하고 미사용"으로 보지만 런타임에 RFC가 사용하므로 제거 금지.
  // @ts-ignore -- RFC runtime uses this via ref; TS incorrectly flags it as unused
  private getSelectionState: (...args: unknown[]) => unknown;
  private saveNodeType: <T>(idx: number) => React.Ref<T>;
  // @ts-ignore -- RFC runtime uses this via ref; TS incorrectly flags it as unused
  private terminate: (...args: unknown[]) => unknown;
  /** 헷지 공통 ratio 박스 ref — 드롭다운 위치 계산에 사용 */
  private hedgeRatioBoxRef = React.createRef<TPSLRatioBox>();
  /** 헷지 드래그 핸들 호버 여부 — true일 때만 disablePan 적용해 차트 pan과 충돌 방지 */
  private hedgeHandleHovering = false;
  /** 박스 겹침 시 동일 click 이벤트로 delete가 중복 호출되는 것을 막는 플래그.
   * RFC가 click을 모든 컴포넌트에 전파하므로 첫 번째 delete 이후 같은 tick의 추가 호출을 무시. */
  private _deleteConsumedThisTick = false;

  public constructor(props: InteractiveOrderLineProps) {
    super(props);

    this.terminate = terminate.bind(this);
    this.saveNodeType = saveNodeType.bind(this);
    this.getSelectionState = isHoverForInteractiveType('orderLineList').bind(this);

    this.state = {};
  }

  public render() {
    const { orderLineList, onDoubleClick, isDark, setRatioDropdown } = this.props;
    const { override } = this.state;

    const nodes = orderLineList.map((each, idx) => {
      if (idx === override?.index) return { ...each, ...override };
      return each;
    });

    // long+short 노드 분류
    // hedgePairIds/hedgePairs는 override가 적용되지 않은 원본 orderLineList 기준으로 계산한다.
    // 드래그 중에 hedgeDragOverrides로 yValue가 달라지면 long/short yValue가 불일치하게 되어
    // hedgePairs가 비어버리고 GenericChartComponent(드래그 히트 영역)가 언마운트되는 문제를 방지.
    const hedgePairIds = new Set<string>();
    const rawLongNodes = orderLineList.filter(n => n.positionType === 'long');
    const rawShortNodes = orderLineList.filter(n => n.positionType === 'short');

    // 롱+숏이 모두 존재하면 방향 무관하게 전체를 헷지 멤버로 처리 (숏 왼쪽, 롱 오른쪽 배치)
    const hasBothDirections = rawLongNodes.length > 0 && rawShortNodes.length > 0;
    if (hasBothDirections) {
      rawLongNodes.forEach(n => hedgePairIds.add(n.uuid));
      rawShortNodes.forEach(n => hedgePairIds.add(n.uuid));
    }

    type HedgePairInfo = {
      entryY: number;
      longId: string;
    };
    const hedgePairs: HedgePairInfo[] = [];

    // 공통 주황 진입선/ratio박스: override 적용 후 yValue가 동일한 페어만 (헷지 확정 상태)
    // nodes(override 적용)를 기준으로 계산해야 드래그 중 한쪽이 이동했을 때 즉시 헷지 해제된다.
    const overrideLongNodes = nodes.filter(n => n.positionType === 'long');
    const overrideShortNodes = nodes.filter(n => n.positionType === 'short');
    for (const lng of overrideLongNodes) {
      const match = overrideShortNodes.find(s => s.yValue === lng.yValue);
      if (match) {
        hedgePairs.push({ entryY: lng.yValue ?? 0, longId: lng.uuid });
      }
    }

    // 헷지 공통 ratio박스 표시 조건: 헷지 페어가 있고, long/short의 마지막 TPSL 비율이 동일
    const isHedgePaired = hedgePairs.length > 0;
    // 헷지 멤버 중 하나라도 체결된 상태면 드래그 핸들 비활성화
    const isAnyHedgeEntered = orderLineList.some(n => hedgePairIds.has(n.uuid) && n.isEntered === true);
    const lastTPSLByDirection = this.props.lastTPSLByDirection ?? { long: 0, short: 0, hedge: 0 };
    // long > 0 && short > 0: 양쪽 다 TP가 있을 때만 공통 박스 표시 (한쪽 SL 삭제 시 0이 되므로 숨김)
    // toFixed(2) 비교: 드래그 후 부동소수점 재계산 오차 허용 (UI 표기 기준과 일치)
    const hedgeRatioEqual =
      isHedgePaired &&
      lastTPSLByDirection.long > 0 &&
      lastTPSLByDirection.short > 0 &&
      lastTPSLByDirection.long.toFixed(2) === lastTPSLByDirection.short.toFixed(2);

    // ── 헷지 그룹 레이아웃 (렌더 전 미리 계산 — shared state 의존 없음) ──────────
    // 레이아웃 순서 (차트 좌측 → 우측):
    //   [숏RATIO] --8-- [숏HANDLE] --8-- [숏ENTRY] --16-- [롱RATIO] --8-- [롱HANDLE] --8-- [롱ENTRY]
    const SLOT_GAP = 8;
    const GROUP_GAP = 16;
    const font = `bold ${ORDER_LINE_RENDER_DEFAULTS.fontSize}px ${ORDER_LINE_RENDER_DEFAULTS.fontFamily}`;

    const shortNode = rawShortNodes[0];
    const longNode = rawLongNodes[0];
    const shortAmount = shortNode?.amount ?? 0;
    const longAmount = longNode?.amount ?? 0;

    // ratio 박스는 'TP/SL' 텍스트 기준 고정 너비 (롱/숏 동일)
    const ratioBoxW = calcRatioBoxWidth();
    const handleW = calcLabelWidth('SL', font); // SL > TP 너비이므로 SL 기준
    const shortEntryW = calcEntryBoxWidth(shortAmount, 'short');
    const longEntryW = calcEntryBoxWidth(longAmount, 'long');

    // Y축(우측) 기준 레이아웃 — 가까운 순:
    // Y축 --28-- [헷지드래그핸들] --8-- [롱entry] --8-- [롱HANDLE] --8-- [롱ratio] --16-- [숏entry] --8-- [숏HANDLE] --8-- [숏ratio] --8-- [헷지X]
    const Y_AXIS_MARGIN = 28;
    const longEntryRight = Y_AXIS_MARGIN + HEDGE_DRAG_HANDLE_W + SLOT_GAP;
    const longRatioRight = longEntryRight + longEntryW + SLOT_GAP + handleW + SLOT_GAP;
    const shortEntryRight = longRatioRight + ratioBoxW + GROUP_GAP;
    const shortRatioRight = shortEntryRight + shortEntryW + SLOT_GAP + handleW + SLOT_GAP;
    const hedgeXRight = shortRatioRight + ratioBoxW + SLOT_GAP;

    // 공통 ratio 박스 위치 = 숏 RATIO 슬롯 (개별/공통 동시에 표시될 일 없음)
    const hedgeRatioRightOffset = shortRatioRight;

    return (
      <g>
        {/* 헷지 공통 주황 진입선(PriceLine만):
            z-order상 가장 아래에 와야 하므로 가장 먼저 mount.
            TPSLRatioBox는 개별 박스들보다 위에 그려야 하므로 2패스 이후로 분리 — 아래 참조.
            longId(불변)를 key로 써서 yValue가 변해도 재마운트되지 않는다. */}
        {hasBothDirections &&
          longNode &&
          (() => {
            const longId = longNode.uuid;
            const entryY = hedgePairs[0]?.entryY ?? longNode.yValue ?? 0;
            return (
              <PriceLine
                key={`hedge-entry-${longId}`}
                yValue={isHedgePaired ? entryY : -9999}
                strokeStyle={COLORS.hedge}
                strokeDasharray="ShortDot"
                strokeWidth={1}
                edge={{ ...ORDER_LINE_RENDER_DEFAULTS.edge, stroke: COLORS.hedge, fill: '#FFFFFF' }}
                textFill={COLORS.hedge}
                fontFamily={ORDER_LINE_RENDER_DEFAULTS.fontFamily}
                fontSize={ORDER_LINE_RENDER_DEFAULTS.fontSize}
              />
            );
          })()}

        {/*
         * 2-패스 렌더링: z-order 보장
         *
         * react-financial-charts의 mouse canvas는 마우스 이벤트마다 clear 후 구독 컴포넌트를
         * "mount 순서"대로 다시 그린다. 문제는 TP/SL PriceLine이 조건부로 mount/unmount되면
         * 재mount 시 구독 목록 맨 뒤로 밀려, Entry/Ratio 박스보다 나중에 그려져 위로 올라온다.
         *
         * 해결: 노드 전체를 두 번 순회한다.
         *   1패스(renderBoxesOnly=false): 모든 노드의 PriceLine(오더라인·TP/SL 점선)만 먼저 그림
         *   2패스(renderBoxesOnly=true):  모든 노드의 Entry/Ratio/TP/SL 박스·핸들을 나중에 그림
         *
         * 이렇게 하면 "롱 박스 → 숏 박스" 순으로 mount된 뒤에 TP/SL 라인이 뒤늦게 mount되는
         * 타이밍 문제가 사라지고, 박스가 항상 라인 위에 그려지는 순서가 보장된다.
         */}
        {/* 1패스(lines) → 2패스(boxes) 순으로 렌더 — z-order 보장 */}
        {([false, true] as const).flatMap(renderBoxesOnly =>
          nodes.map((eachNode, idx) => {
            // OrderLineRenderNode는 OrderLineRenderStyle을 intersection으로 포함하므로
            // 스타일 필드(bgFill, stroke, textBox, edge 등)가 정적 타입에 모두 포함됨.
            // shapeConverter가 ORDER_LINE_RENDER_DEFAULTS를 주입하여 런타임에 항상 채운다.
            const applied = eachNode;
            const isHedgeMember = hedgePairIds.has(applied.uuid ?? '');

            // 헷지 멤버일 때 textBox.closeIcon.right를 고정 레이아웃 값으로 오버라이드
            // → EachInteractiveOrderLine의 entryRightToYAxisDistance/entryLeftToYAxisDistance가 상수 기반으로 계산됨
            // isHedgePaired(yValue 동일 페어) 조건 대신 hasBothDirections(롱+숏 둘 다 존재) 조건 사용.
            // entry 드래그로 yValue가 달라지거나 enter long → enter short 직후처럼
            // yValue가 불일치한 상태에서도 고정 레이아웃을 유지해야 하기 때문.
            const hedgeTextBox =
              hasBothDirections && isHedgeMember
                ? {
                    ...applied.textBox,
                    closeIcon: {
                      ...applied.textBox.closeIcon,
                      right: applied.positionType === 'short' ? shortEntryRight : longEntryRight,
                    },
                  }
                : applied.textBox;

            return (
              <EachInteractiveOrderLine
                key={`${renderBoxesOnly ? 'boxes' : 'lines'}-${applied.uuid}`}
                ref={renderBoxesOnly ? undefined : this.saveNodeType(idx)}
                index={idx}
                {...applied}
                textBox={hedgeTextBox}
                isDark={isDark}
                hideEntryLine={isHedgePaired && isHedgeMember}
                isHedge={isHedgeMember}
                hideRatioBox={isHedgeMember && hedgeRatioEqual}
                onDelete={this.handleDelete}
                onDrag={this.handleDrag}
                onDragComplete={this.handleDragComplete}
                onDoubleClick={onDoubleClick}
                setRatioDropdown={setRatioDropdown}
                renderBoxesOnly={renderBoxesOnly}
              />
            );
          }),
        )}

        {/* 헷지 공통 ratio 박스:
            EachInteractiveOrderLine 2패스(박스) 이후에 mount — 개별 박스들보다 위에 그려짐.
            PriceLine(진입선)은 위 블록에서 먼저 mount했으므로 z-order: 진입선 < 개별박스 < 헷지ratio박스.
            hedgeRatioEqual일 때만 마운트 (한쪽 SL 삭제 시 언마운트) — z-order 제약 없으므로 조건부 마운트 가능. */}
        {hasBothDirections &&
          longNode &&
          hedgeRatioEqual &&
          (() => {
            const longId = longNode.uuid;
            const entryY = hedgePairs[0]?.entryY ?? longNode.yValue ?? 0;
            return (
              <TPSLRatioBox
                ref={this.hedgeRatioBoxRef}
                key={`hedge-ratio-${longId}`}
                yValue={entryY}
                color={COLORS.hedge}
                ratioText={lastTPSLByDirection.hedge.toFixed(2)}
                isDark={isDark ?? false}
                rightOffset={hedgeRatioRightOffset}
                onHover={(_e, moreProps) => {
                  const hovering = moreProps.hovering ?? false;
                  const {
                    chartConfig: { yScale },
                  } = moreProps;
                  const boxX = this.hedgeRatioBoxRef.current?.boxX ?? 0;
                  const boxY = Math.round(yScale(entryY)) - 12;
                  setRatioDropdown({
                    uuid: '',
                    hovering,
                    position: { x: boxX, y: boxY },
                    positionType: 'hedge',
                    color: COLORS.hedge,
                  });
                }}
                onUnHover={(_e, moreProps) => {
                  if (!moreProps.hovering) setRatioDropdown({ hovering: false });
                }}
                onClick={(_e, moreProps) => {
                  if (!moreProps.hovering) return;
                  const {
                    chartConfig: { yScale },
                  } = moreProps;
                  const boxX = this.hedgeRatioBoxRef.current?.boxX ?? 0;
                  const boxY = Math.round(yScale(entryY)) - 12;
                  setRatioDropdown({
                    uuid: '',
                    hovering: true,
                    position: { x: boxX, y: boxY },
                    positionType: 'hedge',
                    color: COLORS.hedge,
                  });
                }}
              />
            );
          })()}

        {/* 헷지 드래그 핸들 + X버튼: hasBothDirections 동안 항상 마운트 유지 — z-order 보장.
            isHedgePaired가 false일 때는 isHover=false, canvasDraw에서 skip으로 숨긴다. */}
        {hasBothDirections &&
          longNode &&
          (() => {
            const longId = longNode.uuid;
            const entryY = hedgePairs[0]?.entryY ?? longNode.yValue ?? 0;
            const dragBx = (width: number) => width - HEDGE_DRAG_HANDLE_RIGHT - HEDGE_DRAG_HANDLE_W;
            const dragCy = (yScale: (v: number) => number) => Math.round(yScale(entryY));
            return (
              <>
                <GenericChartComponent
                  key={`hedge-drag-handle-${longId}`}
                  clip={false}
                  canvasToDraw={getMouseCanvas}
                  drawOn={['mousemove', 'mouseleave', 'pan', 'drag']}
                  // 호버 중일 때만 pan 차단 → 핸들 드래그와 차트 pan 동시 발생 방지
                  // 호버 해제 시 pan 복원돼 일반 차트 조작에는 영향 없음
                  disablePan={this.hedgeHandleHovering}
                  onHover={() => {
                    if (!isHedgePaired || isAnyHedgeEntered) return;
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                    (this.context as { setCursorClass: (c: string | null) => void }).setCursorClass(
                      'react-financial-charts-pointer-cursor',
                    );
                    if (!this.hedgeHandleHovering) {
                      this.hedgeHandleHovering = true;
                      this.forceUpdate();
                    }
                  }}
                  onUnHover={() => {
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                    (this.context as { setCursorClass: (c: string | null) => void }).setCursorClass(null);
                    if (this.hedgeHandleHovering) {
                      this.hedgeHandleHovering = false;
                      this.forceUpdate();
                    }
                  }}
                  isHover={(moreProps: MoreProps) => {
                    if (!isHedgePaired || isAnyHedgeEntered) return false;
                    const {
                      chartConfig: { width, yScale },
                      mouseXY,
                    } = moreProps;
                    if (!mouseXY) return false;
                    const [mx, my] = mouseXY;
                    const hx = dragBx(width);
                    const hy = dragCy(yScale);
                    return (
                      mx >= hx &&
                      mx <= hx + HEDGE_DRAG_HANDLE_W &&
                      my >= hy - HEDGE_DRAG_HANDLE_H / 2 &&
                      my <= hy + HEDGE_DRAG_HANDLE_H / 2
                    );
                  }}
                  onMouseDown={(e: React.MouseEvent, moreProps: MoreProps) => {
                    if (!isHedgePaired || isAnyHedgeEntered) return;
                    const {
                      chartConfig: { yScale },
                      mouseXY,
                    } = moreProps;
                    if (!mouseXY) return;
                    const [mx, my] = mouseXY;
                    const currentWidth = (moreProps.chartConfig as { width: number }).width;
                    const hx = dragBx(currentWidth);
                    const hy = dragCy(yScale);
                    if (
                      !(
                        mx >= hx &&
                        mx <= hx + HEDGE_DRAG_HANDLE_W &&
                        my >= hy - HEDGE_DRAG_HANDLE_H / 2 &&
                        my <= hy + HEDGE_DRAG_HANDLE_H / 2
                      )
                    )
                      return;
                    e.stopPropagation();
                    e.preventDefault();
                    let lastClientY = e.clientY;
                    const onMove = (ev: PointerEvent) => {
                      const deltaPixels = ev.clientY - lastClientY;
                      lastClientY = ev.clientY;
                      const pixelsPerTick = Math.abs(yScale.invert(hy) - yScale.invert(hy + 1));
                      const deltaPrice = -deltaPixels * pixelsPerTick;
                      const { orderLineList } = this.props;
                      const wouldViolate = orderLineList.some(n => {
                        const newY = (n.yValue ?? 0) + deltaPrice;
                        const tpslPrice = n.tpslPrice;
                        if (!tpslPrice?.[0]) return false;
                        const { tp, sl } = tpslPrice[0];
                        const amount = n.amount ?? 0;
                        const positionType = n.positionType;
                        if (positionType === 'long') {
                          if (sl !== 0 && newY <= sl) return true;
                          if (tp !== 0) {
                            // entry가 TP에 너무 가까워져 TP netROE < 0.1%이 되면 이동 차단
                            const minTPPrice = netRoeToPrice({ netRoe: 0.1, entry: newY, amount, positionType });
                            if (newY >= minTPPrice) return true;
                          }
                        } else {
                          if (sl !== 0 && newY >= sl) return true;
                          if (tp !== 0) {
                            const minTPPrice = netRoeToPrice({ netRoe: 0.1, entry: newY, amount, positionType });
                            if (newY <= minTPPrice) return true;
                          }
                        }
                        return false;
                      });
                      if (wouldViolate) return;
                      const moved = orderLineList.map(n => {
                        const tpsl = n.tpslPrice?.[0];
                        const entryY = n.yValue ?? 0;
                        const newY = entryY + deltaPrice;
                        const amt = n.amount ?? 0;
                        const pos: 'long' | 'short' = n.positionType === 'short' ? 'short' : 'long';
                        return {
                          ...n,
                          yValue: newY,
                          coordinates: [[0, newY]],
                          // TP/SL을 netROE 기반으로 재계산하여 ratio를 정확히 유지
                          ...(tpsl && amt > 0
                            ? {
                                tpslPrice: [
                                  {
                                    tp:
                                      tpsl.tp !== 0
                                        ? netRoeToPrice({
                                            netRoe: priceToNetRoe({
                                              price: tpsl.tp,
                                              entry: entryY,
                                              amount: amt,
                                              positionType: pos,
                                            }),
                                            entry: newY,
                                            amount: amt,
                                            positionType: pos,
                                          })
                                        : 0,
                                    sl:
                                      tpsl.sl !== 0
                                        ? netRoeToPrice({
                                            netRoe: priceToNetRoe({
                                              price: tpsl.sl,
                                              entry: entryY,
                                              amount: amt,
                                              positionType: pos,
                                            }),
                                            entry: newY,
                                            amount: amt,
                                            positionType: pos,
                                          })
                                        : 0,
                                  },
                                ],
                              }
                            : {}),
                        };
                      });
                      for (const n of moved) {
                        if (n.positionType !== 'long' && n.positionType !== 'short') continue;
                        const newY = n.yValue ?? 0;
                        const amount = n.amount;
                        const tpslPrice = n.tpslPrice;
                        if (!tpslPrice?.[0] || !amount) continue;
                        const { tp, sl } = tpslPrice[0];
                        if (tp === 0 || sl === 0) continue;
                        try {
                          const { TP_roe, SL_roe } = calculateTPAndSL({
                            price: newY,
                            amount,
                            tp,
                            sl,
                            positionType: n.positionType,
                          });
                          const ratio = TPSLRatio(SL_roe, TP_roe);
                          globalShapeStore.getState().updateLastTPSLByDirection(n.positionType, ratio);
                        } catch {
                          /* ignore */
                        }
                      }
                      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                      this.props.onDragComplete(null, moved, {} as unknown as MoreProps, null);
                    };
                    const onUp = () => {
                      document.removeEventListener('pointermove', onMove);
                      document.removeEventListener('pointerup', onUp);
                      // 헷지 드래그는 RFC의 drag 시스템을 거치지 않고 document 리스너로 구현됐기 때문에,
                      // RFC EventCapture 입장에서는 dragHappened=false 상태로 click이 발생한다.
                      // 그래서 drop 지점(예: entry의 X 버튼) 위에 있는 GenericComponent들의 onClick이 실행돼
                      // 예기치 않은 삭제/선택 동작이 발생함. capture phase에서 한 번만 click을 차단해 이를 막는다.
                      const blockClick = (ce: Event) => {
                        ce.stopPropagation();
                        ce.preventDefault();
                        document.removeEventListener('click', blockClick, true);
                      };
                      document.addEventListener('click', blockClick, true);
                      // 만약 click이 발생하지 않는 경우(드래그 후 다른 창으로 이동 등) 대비해 다음 tick에 해제
                      setTimeout(() => document.removeEventListener('click', blockClick, true), 0);
                    };
                    document.addEventListener('pointermove', onMove);
                    document.addEventListener('pointerup', onUp);
                  }}
                  canvasDraw={(ctx: CanvasRenderingContext2D, moreProps: MoreProps) => {
                    if (!isHedgePaired || isAnyHedgeEntered) return;
                    const {
                      chartConfig: { width, yScale, height },
                    } = moreProps;
                    const hy = dragCy(yScale);
                    if (hy < 0 || hy > height) return;
                    const hx = dragBx(width);
                    const by2 = hy - HEDGE_DRAG_HANDLE_H / 2;
                    ctx.save();
                    ctx.lineWidth = 1;
                    ctx.strokeStyle = COLORS.hedge;
                    roundedRect(ctx, hx, by2, HEDGE_DRAG_HANDLE_W, HEDGE_DRAG_HANDLE_H, HEDGE_DRAG_HANDLE_RADIUS);
                    ctx.fillStyle = isDark ? '#1f1f1f' : '#FFFFFF';
                    ctx.fill();
                    ctx.stroke();
                    ctx.fillStyle = COLORS.hedge;
                    ctx.strokeStyle = COLORS.hedge;
                    ctx.lineWidth = 1.5;
                    const cx2 = hx + HEDGE_DRAG_HANDLE_W / 2;
                    const aw = 3;
                    const ah = 4;
                    const gap = 2;
                    ctx.beginPath();
                    ctx.moveTo(cx2, hy - gap - ah);
                    ctx.lineTo(cx2 - aw, hy - gap);
                    ctx.lineTo(cx2 + aw, hy - gap);
                    ctx.closePath();
                    ctx.fill();
                    ctx.beginPath();
                    ctx.moveTo(cx2, hy + gap + ah);
                    ctx.lineTo(cx2 - aw, hy + gap);
                    ctx.lineTo(cx2 + aw, hy + gap);
                    ctx.closePath();
                    ctx.fill();
                    ctx.beginPath();
                    ctx.moveTo(cx2, hy - gap);
                    ctx.lineTo(cx2, hy + gap);
                    ctx.stroke();
                    ctx.restore();
                  }}
                />

                {/* 헷지 X 버튼 */}
                {this.props.onDeleteAll && (
                  <GenericChartComponent
                    key={`hedge-delete-${longId}`}
                    clip={false}
                    canvasToDraw={getMouseCanvas}
                    drawOn={['mousemove', 'mouseleave', 'pan', 'drag']}
                    enableDragOnHover
                    onHover={() => {}}
                    onUnHover={() => {}}
                    isHover={(moreProps: MoreProps) => {
                      if (!isHedgePaired) return false;
                      const {
                        chartConfig: { width, yScale },
                        mouseXY,
                      } = moreProps;
                      if (!mouseXY) return false;
                      const btnCy = Math.round(yScale(entryY));
                      const bx = width - hedgeXRight - HEDGE_DELETE_BTN_W;
                      const [mx, my] = mouseXY;
                      return (
                        mx >= bx &&
                        mx <= bx + HEDGE_DELETE_BTN_W &&
                        my >= btnCy - HEDGE_DELETE_BTN_H / 2 &&
                        my <= btnCy + HEDGE_DELETE_BTN_H / 2
                      );
                    }}
                    onClickWhenHover={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      this.props.onDeleteAll?.();
                    }}
                    canvasDraw={(ctx: CanvasRenderingContext2D, moreProps: MoreProps) => {
                      if (!isHedgePaired) return;
                      const {
                        chartConfig: { width, yScale, height },
                      } = moreProps;
                      const btnCy = Math.round(yScale(entryY));
                      if (btnCy < 0 || btnCy > height) return;
                      const bx = width - hedgeXRight - HEDGE_DELETE_BTN_W;
                      const by = btnCy - HEDGE_DELETE_BTN_H / 2;
                      ctx.save();
                      ctx.lineWidth = 1;
                      ctx.strokeStyle = COLORS.hedge;
                      roundedRect(ctx, bx, by, HEDGE_DELETE_BTN_W, HEDGE_DELETE_BTN_H, HEDGE_DELETE_BTN_RADIUS);
                      ctx.fillStyle = isDark ? '#1f1f1f' : '#FFFFFF';
                      ctx.fill();
                      ctx.stroke();
                      drawCancelIcon(ctx, bx + HEDGE_DELETE_BTN_W / 2, btnCy, COLORS.hedge);
                      ctx.restore();
                    }}
                  />
                )}
              </>
            );
          })()}
      </g>
    );
  }

  private readonly handleDelete = (
    e: React.MouseEvent | null,
    index: number | undefined,
    moreProps: MoreProps,
    type: 'Entry' | 'TP' | 'SL',
  ) => {
    // 박스가 겹쳐 동일 click 이벤트에서 여러 컴포넌트의 handleDelete가 연속 호출될 때
    // 첫 번째만 처리하고 나머지는 무시한다.
    if (this._deleteConsumedThisTick) return;
    this._deleteConsumedThisTick = true;
    setTimeout(() => {
      this._deleteConsumedThisTick = false;
    }, 0);

    const { onDelete, orderLineList } = this.props;
    if (onDelete !== undefined && index !== undefined) {
      onDelete(e, orderLineList[index], moreProps, type);
    }
    if (type === 'Entry') {
      this.setState({
        override: undefined,
      });
    }
  };

  private readonly handleDragComplete = (e: React.MouseEvent, moreProps: MoreProps, type: 'Entry' | 'TP' | 'SL') => {
    const { override } = this.state;
    if (isDefined(override)) {
      const { orderLineList } = this.props;
      const overrideIndex = override.index;

      // ─── Entry 드롭 완료: 3px 이내 헷지 페어 동기화 ──────────────
      // 드래그한 노드가 Entry이고, 반대 방향 노드와 픽셀 거리가 3px 이내이면
      // 상대 노드의 yValue를 드래그한 노드의 yValue로 동기화 → 헷지 상태 확정
      let hedgeSyncIndex: number | undefined;
      let hedgeSyncYValue: number | undefined;
      // 드래그한 나 자신을 스냅해야 하는 경우 (체결된 상대방 기준)
      let hedgeSyncSelf = false;
      if (type === 'Entry' && isDefined(override.yValue)) {
        const yScale = moreProps.chartConfig?.yScale;
        const draggedNode = orderLineList[overrideIndex];
        const draggedPositionType = draggedNode.positionType;
        const draggedYValue = override.yValue ?? 0;
        const draggedIsEntered = !!draggedNode.isEntered;

        if (yScale) {
          const draggedY = yScale(draggedYValue);
          orderLineList.forEach((node, idx) => {
            if (idx === overrideIndex) return;
            const isOpposite =
              (draggedPositionType === 'long' && node.positionType === 'short') ||
              (draggedPositionType === 'short' && node.positionType === 'long');
            if (!isOpposite) return;
            const peerY = yScale(node.yValue ?? 0);
            const pixelDist = Math.abs(draggedY - peerY);
            if (pixelDist <= 3) {
              const peerIsEntered = !!node.isEntered;
              if (peerIsEntered || draggedIsEntered) {
                // 하나라도 체결됨: 체결된 쪽 가격으로 미체결 쪽을 스냅
                const settledYValue = peerIsEntered ? (node.yValue ?? 0) : draggedYValue;
                if (peerIsEntered) {
                  // 상대방이 체결됨 → 드래그한 나를 체결 가격으로 스냅 (상대방 불변)
                  hedgeSyncYValue = settledYValue;
                  hedgeSyncSelf = true;
                } else {
                  // 내가 체결됨 → 드래그 자체가 막혀있어야 하지만 방어적으로 처리
                  hedgeSyncIndex = idx;
                  hedgeSyncYValue = settledYValue;
                }
              } else {
                // 둘 다 미체결: 롱 가격으로 숏을 스냅
                const longYValue = draggedPositionType === 'long' ? draggedYValue : (node.yValue ?? 0);
                hedgeSyncIndex = idx; // 숏(상대방 또는 드래그한 나) 인덱스
                hedgeSyncYValue = longYValue;
                // 숏을 드래그한 경우: 나(숏) 대신 상대방(롱)이 기준 → 나를 스냅해야 함
                if (draggedPositionType === 'short') {
                  hedgeSyncIndex = undefined;
                  hedgeSyncSelf = true;
                }
              }
            }
          });
        } else {
          console.warn('[hedge-sync] yScale not available in moreProps.chartConfig');
        }
      }

      /** entry 가격이 바뀔 때 기존 ratio를 유지하며 tpslPrice 재계산 */
      const recalcTpslForNewEntry = (node: (typeof orderLineList)[number], oldEntry: number, newEntry: number) => {
        const tpsl = node.tpslPrice?.[0];
        const amt = node.amount ?? 0;
        const pos: 'long' | 'short' = node.positionType === 'short' ? 'short' : 'long';
        if (!tpsl || amt <= 0) return node.tpslPrice;
        return [
          {
            tp:
              tpsl.tp !== 0
                ? netRoeToPrice({
                    netRoe: priceToNetRoe({ price: tpsl.tp, entry: oldEntry, amount: amt, positionType: pos }),
                    entry: newEntry,
                    amount: amt,
                    positionType: pos,
                  })
                : 0,
            sl:
              tpsl.sl !== 0
                ? netRoeToPrice({
                    netRoe: priceToNetRoe({ price: tpsl.sl, entry: oldEntry, amount: amt, positionType: pos }),
                    entry: newEntry,
                    amount: amt,
                    positionType: pos,
                  })
                : 0,
          },
        ];
      };

      const newAlertList = orderLineList.map((each, idx) => {
        const selected = override.type === type && idx === overrideIndex;
        if (idx === overrideIndex) {
          switch (type) {
            case 'TP':
              return { ...each, tpslPrice: override.tpslPrice, selected };
            case 'SL':
              return { ...each, tpslPrice: override.tpslPrice, selected };
            case 'Entry':
            default: {
              const finalY = hedgeSyncSelf ? (hedgeSyncYValue ?? 0) : (override.yValue ?? 0);
              // hedgeSyncSelf: 스냅으로 entry가 이동하므로 원래 entry 기준으로 tpslPrice 재계산
              const finalTpslPrice = hedgeSyncSelf
                ? recalcTpslForNewEntry(each, each.yValue ?? 0, finalY)
                : (override.tpslPrice ?? each.tpslPrice);
              return {
                ...each,
                yValue: finalY,
                coordinates: [[0, finalY]],
                tpslPrice: finalTpslPrice,
                selected,
              };
            }
          }
        } else if (idx === hedgeSyncIndex) {
          // 3px 이내 페어 노드: yValue를 드래그한 노드와 동기화
          // coordinates도 함께 업데이트해야 restoreFromStorage 시 yValue가 덮어씌워지지 않음
          // entry 이동에 맞춰 tpslPrice도 ratio 유지하며 재계산
          return {
            ...each,
            yValue: hedgeSyncYValue,
            coordinates: [[0, hedgeSyncYValue ?? 0]],
            tpslPrice: recalcTpslForNewEntry(each, each.yValue ?? 0, hedgeSyncYValue ?? 0),
            selected: false,
          };
        } else {
          return { ...each, selected };
        }
      });
      const draggedAlert = newAlertList[overrideIndex];

      // 드래그 완료 후 해당 방향의 lastTPSLByDirection 업데이트
      // Entry/TP/SL 드래그 모두 비율이 바뀔 수 있으므로 항상 재계산
      {
        const draggedNode = newAlertList[overrideIndex];
        const posDir = draggedNode.positionType === 'short' ? 'short' : 'long';
        const entryY = draggedNode.yValue ?? 0;
        const tpslPrice = draggedNode.tpslPrice;
        if (tpslPrice?.[0] && tpslPrice[0].sl !== 0 && tpslPrice[0].tp !== 0 && entryY !== 0 && draggedNode.amount) {
          try {
            const { TP_roe, SL_roe } = calculateTPAndSL({
              price: entryY,
              amount: draggedNode.amount,
              tp: tpslPrice[0].tp,
              sl: tpslPrice[0].sl,
              positionType: posDir,
            });
            const ratio = TPSLRatio(SL_roe, TP_roe);
            globalShapeStore.getState().updateLastTPSLByDirection(posDir, ratio);
          } catch {
            // 유효하지 않은 값이면 무시
          }
        }
      }

      this.setState(
        {
          override: undefined,
        },
        () => {
          const { onDragComplete } = this.props;
          if (onDragComplete !== undefined) {
            onDragComplete(e, newAlertList, moreProps, draggedAlert);
          }
        },
      );
    }
  };

  private readonly handleDrag = (
    _: React.MouseEvent,
    index: number | undefined,
    yCordinateValue: number,
    type: 'Entry' | 'TP' | 'SL',
  ) => {
    const { orderLineList } = this.props;
    if (index === undefined) return;
    const current = orderLineList[index];

    if (type === 'Entry') {
      // Entry 드래그 시 TP/SL을 netROE 기반으로 재계산하여 ratio를 정확히 유지한다.
      const entryY = current.yValue ?? 0;
      const amount = current.amount ?? 0;
      const positionType = current.positionType === 'short' ? 'short' : 'long';
      const rawTpsl = current.tpslPrice ?? [];
      const currentTpsl = rawTpsl.length > 0 ? rawTpsl : [{ tp: 0, sl: 0 }];
      const updatedTpsl =
        amount > 0
          ? currentTpsl.map(pair => ({
              tp:
                pair.tp !== 0
                  ? netRoeToPrice({
                      netRoe: priceToNetRoe({ price: pair.tp, entry: entryY, amount, positionType }),
                      entry: yCordinateValue,
                      amount,
                      positionType,
                    })
                  : 0,
              sl:
                pair.sl !== 0
                  ? netRoeToPrice({
                      netRoe: priceToNetRoe({ price: pair.sl, entry: entryY, amount, positionType }),
                      entry: yCordinateValue,
                      amount,
                      positionType,
                    })
                  : 0,
            }))
          : currentTpsl;
      this.setState({
        override: {
          index,
          type,
          yValue: yCordinateValue,
          tpslPrice: updatedTpsl,
        },
      });
    } else {
      // For TP/SL drag, update the tpslPrice array
      const rawTpsl = current.tpslPrice ?? [];
      const currentTpsl = rawTpsl.length > 0 ? rawTpsl : [{ tp: 0, sl: 0 }];
      const updatedTpsl = currentTpsl.map(pair =>
        type === 'TP' ? { ...pair, tp: yCordinateValue } : { ...pair, sl: yCordinateValue },
      );
      this.setState({
        override: {
          index,
          type,
          tpslPrice: updatedTpsl,
        },
      });
    }
  };
}
